import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useDataContex } from "../../../hooks/Context/PatientProvider";
import Swal from "sweetalert2";
const CalendarContainer = styled.div`
  font-family: "Roboto", sans-serif;
  box-shadow: 1px 1px 7px 1px #00000029;
  border-radius: 10px;
  width: 70%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    width: 80%;
    margin: 0 auto;
  }

  @media (max-width: 500px) {
    width: 100%;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;

  .añoActual {
    background-color: #eaeaea;
    padding: 0.2rem 1rem;
    border-radius: 30px;
    color: #707070;
    font-weight: bold;
  }
`;

const MonthButton = styled.button`
  width: 2rem;
  height: 2rem;
  background-color: #eaeaea;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: #707070;
  margin-left: 1rem;
`;

const MonthDropdown = styled.select`
  background-color: #eaeaea;
  border: none;
  border-radius: 20px;
  padding: 0.25rem;
  cursor: pointer;
  color: #707070;
  font-weight: bold;
  text-align: center;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
  padding: 0.5rem;

  @media (max-width: 320px) {
    grid-template-columns: repeat(7, 1fr);
    gap: 0.25rem;
  }
`;

const DayHeader = styled.div`
  font-weight: bold;
  text-align: center;
  color: #707070;
  width: 35px;
  height: 36px;
  margin: 0 auto;
`;

const Day = styled.div<{
  $isselected?: boolean;
  $istoday?: boolean;
  $isselectable?: boolean;
  $isspecial?: boolean;
}>`
  text-align: center;
  color: #707070;
  font-weight: bold;
  cursor: pointer;
  border-radius: 50%;
  width: 35px;
  height: 36px;
  line-height: 36px;
  margin: 0 auto;
  ${({ $isselected }) =>
    $isselected === true
      ? `
      background-color: #0181AE;
      color: white;
    `
      : `
      &:hover {
        background-color: #f5f5f5;
      }
    `}

  ${({ $istoday }) =>
    $istoday === true &&
    `
    border: 1px solid #0181AE;
  `}
`;

const months = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const daysOfWeek = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const Calendar: React.FC = () => {
  const now = new Date();
  const [currentDate, setCurrentDate] = useState<Date>(now);
  const [selectedDate, setSelectedDate] = useState<Date>(now);

  const [fechasDisponible, setFechasDisponible] = useState<string[]>([]);

  const { setCalendarData, CalendarData, HorariosData } = useDataContex();
  const [hoursData, setHours] = useState([]);

  var myHeaders = new Headers();
  myHeaders.append("accept", "*/*");
  myHeaders.append(
    "Content-Type",
    "application/json;odata.metadata=minimal;odata.streaming=true"
  );

  const formatFecha = (fecha) => {
    const partes = fecha.split("/");
    return `${partes[2]}-${partes[0]}-${partes[1]}`;
  };

  useEffect(() => {
    if (HorariosData && Array.isArray(HorariosData.data)) {
      const fechasMapeadas = HorariosData.data.map((item) => item.date);
      setFechasDisponible(fechasMapeadas);
    }

    // Verifica si CalendarData está vacío
    if (CalendarData.length === 0) {
      const initialDate = {
        DateCalendar: {
          campo: formatDate(now),
          valido: true,
          hours: hoursData,
          date: now.toISOString().split("T")[0],
        },
      };

      setCalendarData([initialDate]);
      handleDayClick(now.getDate());
    }
  }, [HorariosData, CalendarData]);

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(months.indexOf(event.target.value));
    setCurrentDate(newDate);
  };

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  const isSpecialDate = (date) => {
    const anio = date.getFullYear();
    const mes = (date.getMonth() + 1).toString().padStart(2, "0");
    const dia = date.getDate().toString().padStart(2, "0");
    const formattedDate = `${anio}-${mes}-${dia}`;

    return fechasDisponible.includes(formattedDate);
  };

  const days = Array.from({ length: daysInMonth }, (_, index) => index + 1);
  const startOffset = Array.from({ length: firstDayOfMonth }, (_, index) => "");

  const formatDate = (date: Date): string => {
    const dayName = [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado",
    ];
    const monthName = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    return `${dayName[date.getDay()]} ${date.getDate()} de ${
      monthName[date.getMonth()]
    } del ${date.getFullYear()}`;
  };

  const handleDayClick = (day: number) => {
    const newSelectedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );

    // Verificar si es domingo o día festivo.
    if (newSelectedDate.getDay() === 0) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Oops...",
        text: "Los domingos no están disponibles para selección.",
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#ec2323",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const formatte = newSelectedDate.toISOString().split("T")[0];
    const date = new Date();
    date.setDate(date.getDate() + 15);
    const date15 = date.toISOString().split("T")[0];
    if (formatte > date15) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Oops...",
        text: "Solo puedes seleccionar una fecha dentro de los próximos 15 días.",
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#ec2323",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    // Lista ficticia de días festivos.newSelectedDate Ajusta según tus necesidades. en este caso 2023
    const holidays = [
      "2024-01-01",
      "2024-03-29",
      "2024-03-30",
      "2024-05-01",
      "2024-05-14",
      "2024-06-04",
      "2024-06-11",
      "2024-07-20",
      "2024-08-07",
      "2024-12-08",
      "2024-12-25",
    ];
    if (holidays.includes(formatte)) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Oops...",
        text: "Los días festivos no están disponibles para selección.",
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#ec2323",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }
    setSelectedDate(newSelectedDate);

    const formattedDate = formatDate(newSelectedDate);
    Swal.fire({
      position: "top-end",
      icon: "success",
      title: "Bien!",
      text: "Fecha seleccionada: " + formattedDate,
      showConfirmButton: false,
      timer: 2000,
    });

    var myHeaders = new Headers();
    myHeaders.append("accept", "*/*");
    myHeaders.append(
      "Content-Type",
      "application/json;odata.metadata=minimal;odata.streaming=true"
    );

    var raw = {
      areaAfectada: "Mano",
      fromDate: formatte.toString(),
      toDate: formatte.toString(),
    };

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(raw),
      redirect: "follow" as RequestRedirect,
    };
    fetch("/apihub/legacy/Service/Agenda/HorariosDisponibles", requestOptions)
      .then((response) => response.json())
      .then((result) => {
        const availableHours = result.data[0].areas[5].hours;

        // Actualiza el estado de 'hoursData'
        setHours(availableHours);
        const newFormData = {
          DateCalendar: {
            campo: formattedDate,
            valido: true,
            date: formatte,
            hours: availableHours,
          },
        };
        setCalendarData([newFormData]);
      })
      .catch((error) => console.log("error", error));
  };
  return (
    <>
      <CalendarContainer>
        <CalendarHeader>
          <div>
            <MonthButton onClick={handlePrevMonth}>&lt;</MonthButton>
            <MonthButton onClick={handleNextMonth}>&gt;</MonthButton>
          </div>
          <MonthDropdown
            value={months[currentDate.getMonth()]}
            onChange={handleMonthChange}
          >
            {months.map((month, index) => (
              <option key={index} value={month}>
                {month}
              </option>
            ))}
          </MonthDropdown>
          <div className="añoActual">{currentDate.getFullYear()}</div>
        </CalendarHeader>
        <CalendarGrid>
          {daysOfWeek.map((day) => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
          {startOffset.map((_, index) => (
            <Day key={`empty-${index}`} />
          ))}
          {days.map((day) => {
            // horarios disponibles
            const date = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              day
            );

            const isSpecial = isSpecialDate(date);

            const isSelectable =
              currentDate.getFullYear() > now.getFullYear() ||
              (currentDate.getMonth() >= now.getMonth() &&
                (day >= now.getDate() ||
                  currentDate.getMonth() > now.getMonth()));
            const isSelected =
              day === selectedDate.getDate() &&
              currentDate.getMonth() === selectedDate.getMonth() &&
              currentDate.getFullYear() === selectedDate.getFullYear();
            const handleClick = () => {
              if (isSelectable) {
                handleDayClick(day);
              }
            };

            return (
              <Day
                key={day}
                // Agregar propiedades adicionales para el estilo
                $isselected={isSelected}
                $isspecial={isSpecial} // Nueva propiedad para días especiales
                onClick={handleClick}
                style={
                  isSpecial
                    ? { border: "1px solid #707070" }
                    : {
                        backgroundColor: "#707070",
                        opacity: "0.5",
                        cursor: "not-allowed",
                        color: "white",
                      }
                }
              >
                {day}
              </Day>
            );
          })}
        </CalendarGrid>
      </CalendarContainer>
    </>
  );
};

export default Calendar;
